import React, { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, Sparkles, Trash2, Printer, RefreshCw, Plus, Pencil, Save } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka } from "../../components/ui.jsx";
import { apiFetch } from "../../lib/api.js";
import AIGenerating, { odhadPct } from "../../components/AIGenerating.jsx";
import { LOGO_SVG_TMAVE, LOGO_SVG_BILE } from "../../lib/brand.js";

import { getCurrentUser } from "../../lib/auth.js";
import { spocitejPrijemJs, prijemNaFormular, kcalZMaker, cilFaktorText } from "../../lib/prijem.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { obsahujeSurovinu } from "../../lib/surovinaShoda.js";

// PDF jídelníček na měsíc (14.7.2026) — sekce výživové specialistky.
// Z dotazníku klientky vygeneruje obsáhlý dokument: 4 týdny jídel (každý den
// všechny chody dle zvoleného počtu), 5 bonusových receptů ke každému chodu,
// přesné týdenní nákupní seznamy a bonusové kapitoly na míru (podklady z blogu
// ladislavzatecka.cz). PDF vznikne tiskem z prohlížeče (tlačítko v náhledu).
// Historie všech vygenerovaných dokumentů je auditní: vygenerovaný dokument
// smaže jen owner, nutritionist maže jen koncepty.

function fmtDT(d) {
  try {
    const x = new Date(d);
    return `${x.toLocaleDateString("cs-CZ")} ${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
}

// Skloňování: 1 chod / 2-4 chody / 5+ chodů (0 taky chodů).
const sklonChod = (n) => { const c = Number(n) || 0; return c === 1 ? "chod" : (c >= 2 && c <= 4) ? "chody" : "chodů"; };
const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// ── Tiskový HTML dokument (schválený design 14.7.2026) ──────────────────────
// Návrh znění pro první použití. Jakmile se jednou uloží vlastní text, bere se
// jako výchozí ten poslední použitý (vrací ho /api/pdf-jidelnicek v seznamu).
const UPOZORNENI_NAVRH = "Jídelníček je sestavený na míru podle odpovědí v dotazníku a platí pro aktuální zdravotní stav. Není náhradou lékařské péče. Při těhotenství, kojení, užívání léků nebo onemocnění štítné žlázy jej prosím před zahájením konzultujte se svým lékařem.";
const UPOZORNENI_MAX = 600;

function buildPdfHtml(dok) {
  const d = dok.data || {};
  const jmeno = dok.klient_name || "klientka";
  const krestni = jmeno.split(/\s+/)[0] || jmeno;
  const kcal = d.kalorie || "";
  const makra = d.makra || {};
  const paticka = (str) => `<div class="paticka"><span class="pat-l">${LOGO_SVG_TMAVE}<span class="pill">Jídelníček na míru</span></span><span class="pat-p"><span class="pill">${esc(jmeno)}</span><span class="pill">${str}</span></span></div>`;

  // 4. pad zenskych jmen (13.8.2026): "pripraveny pro Marta Zimmermannová"
  // je spatne cesky. Jednoducha pravidla (á→ou, ie→ii, a→u) pokryji ceska
  // zenska jmena; neznamy tvar se necha beze zmeny.
  const akuzativ = (cele) => String(cele || "").split(/\s+/).map((w) => {
    if (/á$/.test(w)) return w.slice(0, -1) + "ou";
    if (/ie$/.test(w)) return w.slice(0, -2) + "ii";
    if (/a$/.test(w) && w.length > 2) return w.slice(0, -1) + "u";
    return w;
  }).join(" ");

  const jidloHtml = (nazev, r) => `
    <div class="jidlo">
      <div class="jidlo-hlava">
        <span class="chod">${esc(nazev)}</span>
        <span class="jidlo-nazev">${esc(r.nazev)}</span>
        <span class="jidlo-makra"><b>${esc(r.kcal)} kcal</b><span class="sep"></span>B ${esc(r.b)}<span class="sep"></span>S ${esc(r.s)}<span class="sep"></span>T ${esc(r.t)}</span>
      </div>
      <div class="suroviny">${(r.suroviny || []).map((s) => `<span class="surovina">${esc(s)}</span>`).join("")}</div>
      ${r.postup ? `<div class="postup"><b>Postup:</b> ${esc(r.postup)}</div>` : ""}
    </div>`;

  // Kapitoly — dlouhé se dělí na víc stránek (max 3 sekce na stránku),
  // aby obsah nepřetékal přes A4 (14.7.2026 fix rozbité tiskové grafiky).
  // Závěrečná kapitola ("Slovo závěrem") se renderuje jako poslední strana
  // dokumentu v tmavém designu titulky — vyjmout z běžného toku kapitol.
  const vsechnyKapitoly = d.kapitoly || [];
  const zaverKap = vsechnyKapitoly.find((k) => /závěr/i.test(String(k.titul || "")));
  const bezneKapitoly = vsechnyKapitoly.filter((k) => k !== zaverKap);

  const kapitolyHtml = bezneKapitoly.map((k, i) => {
    const sekce = k.sekce || [];
    // Dělení podle skutečné délky textu (audit 14.7.: 3 sekce na stránku
    // přetékaly přes patičku). Rozpočet ~2400 znaků na stránku; první stránka
    // nese i úvod, poslední i tipy.
    const cena = (sx) => 120 + (sx.odstavce || []).reduce((a, o) => a + String(o).length, 0);
    const cenaTipu = k.tipy && k.tipy.length ? 150 + k.tipy.reduce((a, t) => a + String(t).length, 0) : 0;
    const ROZPOCET = 2400;
    const kusy = [[]];
    let naklad = 300 + String(k.uvod || "").length; // hlavička + úvod na 1. stránce
    sekce.forEach((sx, idx) => {
      const c = cena(sx);
      const posledni = idx === sekce.length - 1;
      const rezerva = posledni ? cenaTipu : 0;
      if (kusy[kusy.length - 1].length > 0 && naklad + c + rezerva > ROZPOCET) {
        kusy.push([]);
        naklad = 300; // hlavička pokračování
      }
      kusy[kusy.length - 1].push(sx);
      naklad += c;
    });
    // Tipy se nevejdou k poslednímu kusu → vlastní stránka.
    if (cenaTipu && naklad + cenaTipu > ROZPOCET && kusy[kusy.length - 1].length > 0) kusy.push([]);
    return kusy.map((kus, ki) => `
    <div class="stranka">
      <div class="obsah-flex">
        ${ki === 0 ? `<div class="kap-hlava ${i >= 1 ? "bonus-hlava" : ""}">
          <div class="kap-cislo">${i + 1}</div>
          <div><h2>${esc(k.titul)}</h2></div>
        </div>
        ${k.uvod ? `<p class="kap-uvod">${esc(k.uvod)}</p>` : ""}` : `<div class="pokrac-hlava ${i >= 1 ? "bonus" : ""}">
          <span class="znak">${i + 1}</span> ${esc(k.titul)}
        </div>`}
        ${kus.map((sx) => `
          <h3 class="sek-nadpis">${esc(sx.nadpis)}</h3>
          ${(sx.odstavce || []).map((o) => `<p class="odst">${esc(o)}</p>`).join("")}
        `).join("")}
        ${(ki === kusy.length - 1 && k.tipy && k.tipy.length) ? `<div class="tip-box"><b>Tipy:</b><ul>${k.tipy.map((t) => `<li>${esc(t)}</li>`).join("")}</ul></div>` : ""}
      </div>
      ${paticka(`kapitola ${i + 1}`)}
    </div>`).join("");
  }).join("");

  // Týdny — každý den vlastní stránka (čitelnost tisku), nákupní seznam za týdnem
  const tydnyHtml = (d.tydny || []).map((t) => {
    const dnyHtml = (t.dny || []).map((den) => {
      const soucet = (den.chody || []).reduce((a, ch) => {
        const r = ch.recept || {};
        return { kcal: a.kcal + (+r.kcal || 0), b: a.b + (+r.b || 0), s: a.s + (+r.s || 0), t: a.t + (+r.t || 0) };
      }, { kcal: 0, b: 0, s: 0, t: 0 });
      // 5+ chodů se na A4 nevejde → den se dělí na dvě stránky.
      const chody = den.chody || [];
      const skupiny = chody.length >= 5 ? [chody.slice(0, 3), chody.slice(3)] : [chody];
      return skupiny.map((sk, si) => `
      <div class="stranka">
        <div class="obsah-flex">
          <div class="den-hlava">
            <span class="den">Týden ${t.cislo}<span class="sep"></span>${esc(den.den)}</span>
            <span class="soucet">${si > 0 ? "druhá část dne" : `celkem ≈ ${soucet.kcal} kcal<span class="sep"></span>B ${soucet.b} g<span class="sep"></span>S ${soucet.s} g<span class="sep"></span>T ${soucet.t} g`}</span>
          </div>
          ${sk.map((ch) => jidloHtml(ch.nazev, ch.recept || {})).join("")}
        </div>
        ${paticka(`týden ${t.cislo}, ${esc(den.den)}`)}
      </div>`).join("");
    }).join("");
    // Nakupni seznam se strankuje po kategoriich (13.8.2026, Láďa: polozky
    // pretekaly pres okraj stranky). Kapacita = 2 sloupce x ~27 radku.
    // Adaptivne (13.8.2026): kratky seznam = vzdusny styl, dlouhy seznam se
    // zhusti (trida kompakt), at je cely na jedne strane; strankuje se az
    // kdyz se nevejde ani zhusteny.
    const radkuCelkem = (t.nakupniSeznam || []).reduce((a, k) => a + 2 + (k.polozky || []).length, 0);
    const kompakt = radkuCelkem > 44;
    const KAPACITA_RADKU = kompakt ? 62 : 44;
    const seznamStranky = [];
    if (t.nakupniSeznam && t.nakupniSeznam.length) {
      let akt = [], radku = 0;
      for (const kat of t.nakupniSeznam) {
        const vyska = 2 + (kat.polozky || []).length;
        if (akt.length && radku + vyska > KAPACITA_RADKU) { seznamStranky.push(akt); akt = []; radku = 0; }
        akt.push(kat); radku += vyska;
      }
      if (akt.length) seznamStranky.push(akt);
    }
    const seznamHtml = seznamStranky.map((kusy, ki) => `
      <div class="stranka">
        <div class="obsah-flex">
          ${ki === 0 ? `<div class="kap-hlava"><div class="kap-cislo">🛒</div>
            <div><h2>Nákupní seznam<span class="sep"></span>týden ${t.cislo}</h2><div class="pod">přesně spočítaný ze všech jídel týdne ${t.cislo}</div></div></div>` : `<div class="pokrac-hlava">
            <span class="znak">🛒</span> Nákupní seznam<span class="sep"></span>týden ${t.cislo} (pokračování)
          </div>`}
          <div class="nakup${kompakt ? " kompakt" : ""}">
            ${kusy.map((kat) => `
              <div class="nakup-skupina"><h4>${esc(kat.kategorie)}</h4>
                ${(kat.polozky || []).map((p) => `<li>${esc(p)}</li>`).join("")}
              </div>`).join("")}
          </div>
        </div>
        ${paticka(`nákup týden ${t.cislo}`)}
      </div>`).join("");
    return dnyHtml + seznamHtml;
  }).join("");

  // Bonusové recepty — na stránku se jich vejde tolik, kolik se skutečně
  // vejde (13.8.2026, Láďa: pevné 3 na stránku nechávaly půl stránky prázdné,
  // 5 svačin se vejde v pohodě). Výška karty se odhadne z počtu surovin a
  // délky postupu, do kapacity stránky se skládá hladově.
  const vyskaReceptuMm = (r) => {
    const surovinRadku = Math.max(1, Math.ceil(((r && r.suroviny) || []).length / 3));
    const postupRadku = Math.max(1, Math.ceil(String((r && r.postup) || "").length / 105));
    return 16 + surovinRadku * 8 + postupRadku * 5;
  };
  const bonusHtml = (d.bonusoveRecepty || []).map((b) => {
    const rec = b.recepty || [];
    const kusy = [];
    let akt = [], vyska = 0;
    const KAPACITA_MM = 215; // obsah stránky po odečtení hlavičky a patičky
    for (const r of rec) {
      const v = vyskaReceptuMm(r);
      if (akt.length && vyska + v > KAPACITA_MM) { kusy.push(akt); akt = []; vyska = 0; }
      akt.push(r); vyska += v;
    }
    if (akt.length) kusy.push(akt);
    if (!kusy.length) kusy.push([]);
    return kusy.map((kus, ki) => `
    <div class="stranka">
      <div class="obsah-flex">
        ${ki === 0 ? `<div class="kap-hlava bonus-hlava"><div class="kap-cislo">★</div>
          <div><h2>Zásoba receptů<span class="sep"></span>${esc(b.chod)}</h2><div class="pod">5 náhradních receptů, kterými můžete kterýkoli den nahradit tento chod</div></div></div>` : `<div class="pokrac-hlava bonus">
          <span class="znak">★</span> Zásoba receptů<span class="sep"></span>${esc(b.chod)}
        </div>`}
        ${kus.map((r) => jidloHtml(b.chod, r)).join("")}
      </div>
      ${paticka(`bonusové recepty`)}
    </div>`).join("");
  }).join("");

  // 7.9.2026 (zadání Ládi): vlastní upozornění nutriční terapeutky. Píše se ve
  // formuláři před generováním (Krok 4), tiskne se sem na závěrečnou stranu nad
  // patičku — poslední, co klientka čte, než začne. Vypnuté nebo prázdné =
  // blok se nevykreslí vůbec, po upozornění nezůstane ani nadpis.
  const dotaznikDok = dok.dotaznik || {};
  const upozTxt = String(dotaznikDok._upozorneni || "").trim();
  const upozZap = dotaznikDok._upozorneni_zap !== false;
  const upozorneniHtml = (upozTxt && upozZap)
    ? `<div class="upozorneni"><div class="upoz-nadpis">Upozornění</div>${
        upozTxt.split(/\n{1,}/).map((o) => o.trim()).filter(Boolean)
          .map((o) => `<p>${esc(o)}</p>`).join("")}</div>`
    : "";

  // Závěrečná strana (tmavý design jako titulka — dokument má důstojnou tečku).
  const zaverHtml = zaverKap ? `
    <div class="stranka zaver">
      <div class="logo">${LOGO_SVG_BILE}</div>
      <h2>${esc(zaverKap.titul).replace(/závěrem/i, '<em>závěrem</em>')}</h2>
      ${zaverKap.uvod ? `<p class="kap-uvod">${esc(zaverKap.uvod)}</p>` : ""}
      ${(zaverKap.sekce || []).map((sx) => `
        <h3 class="sek-nadpis">${esc(sx.nadpis)}</h3>
        ${(sx.odstavce || []).map((o) => `<p class="odst">${esc(o)}</p>`).join("")}
      `).join("")}
      ${(zaverKap.tipy && zaverKap.tipy.length) ? `<div class="tip-box"><b>Na cestu:</b><ul>${zaverKap.tipy.map((t) => `<li>${esc(t)}</li>`).join("")}</ul></div>` : ""}
      ${upozorneniHtml}
      <div class="zaver-foot">
        <span>Držíme Vám palce. Tým FitWay40</span>
        <span>www.fitway40.cz</span>
      </div>
    </div>` : "";

  // Obsah
  const kapPolozky = bezneKapitoly.map((k, i) =>
    `<div class="obsah-radek ${i >= 1 ? "bonus" : ""}"><span class="c">${i + 1}</span> ${esc(k.titul)} ${i >= 1 ? '<span class="bonus-stitek">Bonus</span>' : ""}<span class="tecky"></span></div>`).join("");

  return `<!DOCTYPE html>
<html lang="cs"><head><meta charset="UTF-8">
<title>FitWay40 | Jídelníček na míru | ${esc(jmeno)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,600;8..60,700&display=swap" rel="stylesheet">
<style>
  :root { --zelena:#5b4390; --zelena-svetla:#7c5db0; --zelena-pale:#f0ebfa; --tmava:#241430; --sedy-text:#6b6478; --linka:#e4deed; --oranz:#b78a1f; --oranz-pale:#f7f0dd; }
  * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  body { font-family:'DM Sans','Segoe UI',sans-serif; color:var(--tmava); background:#efeaf7; }
  h1,h2,h3,.logo,.kap-cislo,.makro-box .cislo{font-family:'Source Serif 4',Georgia,serif;}
  .stranka { width:210mm; height:296mm; overflow:hidden; margin:10px auto; background:#fff; padding:16mm 16mm 10mm; box-shadow:0 2px 14px rgba(0,0,0,0.12); display:flex; flex-direction:column; }
  .stranka > .obsah-flex { flex:1 1 auto; min-height:0; overflow:hidden; }
  @media print { body{background:#fff} .stranka{box-shadow:none;margin:0;page-break-after:always} .stranka:last-child{page-break-after:auto} .no-print{display:none!important} @page{size:A4;margin:0} }
  .print-btn { position:fixed; top:16px; right:16px; z-index:10; background:var(--zelena-svetla); color:#fff; border:none; border-radius:12px; padding:12px 20px; font-family:'DM Sans',sans-serif; font-size:15px; font-weight:700; cursor:pointer; box-shadow:0 4px 14px rgba(124,93,176,0.45); }
  .cover { display:flex; flex-direction:column; justify-content:space-between; background:linear-gradient(160deg,#3a2154 0%,#241430 55%,#1b0f26 100%); color:#fff; }
  .cover .logo { height:15mm; } .cover .logo svg { height:100%; width:auto; }
  .cover .stitek { display:inline-block; background:rgba(184,162,220,0.15); border:1px solid rgba(184,162,220,0.5); color:#c9b6e8; border-radius:999px; padding:6px 18px; font-size:13px; font-weight:600; letter-spacing:1.5px; text-transform:uppercase; margin-bottom:22px; }
  .cover h1 { font-size:44px; font-weight:800; line-height:1.15; margin-bottom:14px; } .cover h1 em { font-style:normal; color:#b8a2dc; }
  .cover .pro { font-size:20px; font-weight:300; color:#d8c9ec; } .cover .pro b { font-weight:700; color:#fff; }
  .cover-foot { display:flex; justify-content:space-between; align-items:flex-end; font-size:13px; color:#a99cc0; border-top:1px solid rgba(255,255,255,0.15); padding-top:16px; }
  .makra-pas { display:flex; gap:12px; margin-top:34px; }
  .makro-box { flex:1; background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.14); border-radius:16px; padding:14px 16px; }
  .makro-box .cislo { font-size:24px; font-weight:800; color:#b8a2dc; } .makro-box .pop { font-size:11.5px; color:#c1b3d6; text-transform:uppercase; letter-spacing:1px; margin-top:2px; }
  .kap-hlava { display:flex; align-items:center; gap:14px; margin-bottom:7mm; }
  .kap-cislo { width:46px; height:46px; border-radius:14px; background:var(--zelena-pale); color:var(--zelena); font-size:21px; font-weight:800; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .kap-hlava h2 { font-size:25px; font-weight:800; } .kap-hlava .pod { font-size:13px; color:var(--sedy-text); margin-top:2px; }
  .bonus-hlava .kap-cislo { background:var(--oranz-pale); color:var(--oranz); }
  .kap-uvod { font-size:13.5px; color:var(--sedy-text); line-height:1.65; margin-bottom:5mm; }
  .pokrac-hlava { display:flex; align-items:center; gap:10px; margin-bottom:6mm; padding-bottom:8px; border-bottom:2px solid var(--zelena-pale); color:var(--sedy-text); font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:1.2px; }
  .pokrac-hlava .znak { width:24px; height:24px; border-radius:8px; background:var(--zelena-pale); color:var(--zelena); font-size:12px; font-weight:800; display:flex; align-items:center; justify-content:center; letter-spacing:0; }
  .pokrac-hlava.bonus { border-bottom-color:var(--oranz-pale); } .pokrac-hlava.bonus .znak { background:var(--oranz-pale); color:var(--oranz); }
  .zaver { background:linear-gradient(160deg,#3a2154 0%,#241430 55%,#1b0f26 100%); color:#fff; }
  .zaver .logo { height:12mm; } .zaver .logo svg { height:100%; width:auto; }
  .zaver h2 { font-size:34px; font-weight:800; margin:10mm 0 6mm; } .zaver h2 em { font-style:normal; color:#b8a2dc; }
  .zaver .kap-uvod { color:#d8c9ec; font-size:15px; }
  .zaver .sek-nadpis { color:#b8a2dc; margin-top:7mm; }
  .zaver .odst { color:#ded4ec; font-size:13.5px; }
  .zaver .tip-box { background:rgba(184,162,220,0.10); border-left-color:#b8a2dc; color:#ded4ec; }
  .zaver .tip-box b { color:#c9b6e8; }
  .zaver .zaver-foot { margin-top:auto; border-top:1px solid rgba(255,255,255,0.15); padding-top:14px; display:flex; justify-content:space-between; font-size:12px; color:#a99cc0; }
  .sek-nadpis { font-size:16px; font-weight:700; color:var(--zelena); margin:5mm 0 2mm; }
  .odst { font-size:13px; line-height:1.65; color:#3a3448; margin-bottom:3mm; }
  .paticka { margin-top:auto; display:flex; justify-content:space-between; align-items:center; font-size:10.5px; color:#9a90ac; border-top:1px solid var(--linka); padding-top:8px; flex-shrink:0; }
  .paticka .pat-l { display:flex; align-items:center; gap:8px; } .paticka .pat-p { display:flex; align-items:center; gap:6px; }
  .paticka .pat-l svg { height:11px !important; width:auto; opacity:0.75; }
  .paticka .pill { display:inline-block; background:#f6f3fb; border:1px solid var(--linka); border-radius:999px; padding:2px 10px; font-size:10px; font-weight:600; color:#7d7391; white-space:nowrap; }
  /* Svisly oddelovac misto tecky "·" (13.8.2026, pravidlo designu). */
  .sep { display:inline-block; width:1px; height:0.8em; background:currentColor; opacity:0.35; margin:0 7px; vertical-align:-0.08em; }
  .obsah-radek { display:flex; align-items:baseline; gap:10px; padding:11px 4px; border-bottom:1px dashed var(--linka); font-size:15px; }
  .obsah-radek .c { color:var(--zelena-svetla); font-weight:800; width:26px; } .obsah-radek.bonus .c { color:var(--oranz); }
  .obsah-radek .tecky { flex:1; border-bottom:2px dotted #d8cce6; transform:translateY(-4px); }
  .bonus-stitek { font-size:10px; font-weight:700; color:var(--oranz); background:var(--oranz-pale); border-radius:999px; padding:2px 9px; letter-spacing:0.8px; text-transform:uppercase; }
  .den-hlava { display:flex; align-items:center; justify-content:space-between; background:linear-gradient(90deg,var(--zelena) 0%,var(--zelena-svetla) 100%); color:#fff; border-radius:14px; padding:12px 18px; margin:0 0 5mm; }
  .den-hlava .den { font-size:19px; font-weight:800; } .den-hlava .soucet { font-size:12px; font-weight:600; opacity:0.95; }
  .jidlo { border:1px solid var(--linka); border-radius:12px; padding:9px 13px; margin-bottom:3mm; page-break-inside:avoid; }
  .jidlo-hlava { display:flex; align-items:baseline; gap:10px; margin-bottom:5px; flex-wrap:wrap; }
  .chod { font-size:11px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:var(--zelena); background:var(--zelena-pale); border-radius:999px; padding:3px 11px; }
  .jidlo-nazev { font-size:14px; font-weight:700; }
  .jidlo-makra { margin-left:auto; font-size:11.5px; font-weight:600; color:var(--sedy-text); white-space:nowrap; } .jidlo-makra b { color:var(--zelena); font-size:12.5px; }
  .suroviny { display:flex; flex-wrap:wrap; gap:4px; margin:5px 0; }
  .surovina { font-size:10.5px; background:#f6f3fb; border:1px solid var(--linka); border-radius:999px; padding:2px 9px; color:#463a55; }
  .postup { font-size:11px; color:var(--sedy-text); line-height:1.45; } .postup b { color:var(--tmava); }
  .tip-box { background:var(--zelena-pale); border-left:4px solid var(--zelena-svetla); border-radius:0 12px 12px 0; padding:12px 16px; font-size:13px; line-height:1.6; margin-top:5mm; page-break-inside:avoid; }
  .tip-box b { color:var(--zelena); } .tip-box ul { margin:4px 0 0 18px; }
  /* Vzdusnejsi seznam (13.8.2026, Láďa: "zustava hodne mista volneho") —
     vetsi pismo a rozestupy, sloupce vyvazene, stranka vypada zaplnena. */
  /* Vlastni upozorneni na zaverecne strane (7.9.2026) — tise, ale citelne. */
  .upozorneni { margin-top:auto; border:1px solid rgba(180,154,222,0.42); background:rgba(180,154,222,0.10);
    border-radius:9px; padding:11px 14px; page-break-inside:avoid; }
  .upoz-nadpis { font-size:9.5px; letter-spacing:1.1px; text-transform:uppercase; font-weight:800;
    color:#b49ade; margin-bottom:5px; }
  .upozorneni p { font-size:10.5px; line-height:1.55; color:#cdc0e0; }
  .upozorneni p + p { margin-top:5px; }
  .nakup { columns:2; column-gap:12mm; column-fill:balance; margin-top:4mm; }
  .nakup-skupina { break-inside:avoid; margin-bottom:8mm; }
  .nakup-skupina h4 { font-size:14.5px; font-weight:700; color:var(--zelena); text-transform:uppercase; letter-spacing:1px; margin-bottom:8px; }
  .nakup-skupina li { font-size:14px; color:#463a55; list-style:none; padding:7px 0 7px 30px; position:relative; border-bottom:1px dashed #f1edf8; }
  .nakup-skupina li::before { content:""; position:absolute; left:2px; top:10px; width:15px; height:15px; border:2px solid var(--zelena-svetla); border-radius:5px; }
  /* Dlouhy seznam se zhusti, at je na jedne strane (13.8.2026). */
  .nakup.kompakt .nakup-skupina { margin-bottom:5mm; }
  .nakup.kompakt .nakup-skupina h4 { font-size:13px; margin-bottom:5px; }
  .nakup.kompakt .nakup-skupina li { font-size:12.5px; padding:4px 0 4px 26px; }
  .nakup.kompakt .nakup-skupina li::before { width:13px; height:13px; top:7px; }
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">🖨️ Uložit jako PDF</button>
<div class="stranka cover">
  <div class="logo">${LOGO_SVG_BILE}</div>
  <div>
    <div class="stitek">Výživa na míru</div>
    <h1>Váš jídelníček<br><em>na míru</em></h1>
    <div class="pro">připravený pro <b>${esc(akuzativ(jmeno))}</b></div>
    ${kcal ? `<div class="makra-pas">
      <div class="makro-box"><div class="cislo">${esc(kcal)}</div><div class="pop">kcal denně</div></div>
      <div class="makro-box"><div class="cislo">${esc(makra.bilkoviny)} g</div><div class="pop">bílkoviny</div></div>
      <div class="makro-box"><div class="cislo">${esc(makra.sacharidy)} g</div><div class="pop">sacharidy</div></div>
      <div class="makro-box"><div class="cislo">${esc(makra.tuky)} g</div><div class="pop">tuky</div></div>
    </div>` : ""}
  </div>
  <div class="cover-foot">
    <div>Sestavil tým FitWay40 pro ${esc(akuzativ(krestni))}</div>
    <div>${new Date(dok.updated_at || Date.now()).toLocaleDateString("cs-CZ")}<span class="sep"></span>www.fitway40.cz</div>
  </div>
</div>
<div class="stranka">
  <div class="obsah-flex">
    <div class="kap-hlava"><div class="kap-cislo">☰</div>
      <div><h2>Co najdete uvnitř</h2><div class="pod">jídelníček na 4 týdny + zásoba receptů + kapitoly na míru</div></div></div>
    ${kapPolozky}
    <div class="obsah-radek"><span class="c">◆</span> Jídelníček na 4 týdny (${esc(d.pocetChodu)} ${sklonChod(d.pocetChodu)} denně)<span class="tecky"></span></div>
    <div class="obsah-radek"><span class="c">◆</span> Přesné nákupní seznamy pro každý týden<span class="tecky"></span></div>
    <div class="obsah-radek bonus"><span class="c">★</span> Zásoba 5 náhradních receptů ke každému chodu <span class="bonus-stitek">Bonus</span><span class="tecky"></span></div>
    ${zaverKap ? `<div class="obsah-radek"><span class="c">✦</span> ${esc(zaverKap.titul)}<span class="tecky"></span></div>` : ""}
  </div>
  ${paticka("obsah")}
</div>
${kapitolyHtml}
${tydnyHtml}
${bonusHtml}
${zaverHtml}
</body></html>`;
}

// Pole dotazníku pro kontrolní formulář (zrcadlí portálový dotazník).
const DOT_LABELS = {
  pohlavi: "Pohlaví", vek: "Věk", vyska: "Výška (cm)", vaha: "Váha (kg)",
  cil: "Cíl", zivotni_styl: "Životní styl",
  alergie: "Alergie", alergie_jine: "Alergie - jiné",
  vyloucit: "Nechce v jídelníčku", vyloucit_jine: "Nechce - jiné",
  navyky: "Návyky", tekutiny: "Tekutiny (l/den)",
  pocet_jidel: "Jídel denně", mimo_domov: "Mimo domov (jídel/den)", spanek: "Spánek (h)",
  zmena_vahy: "Změna váhy za rok", alkohol: "Alkohol", oblibena: "Oblíbená jídla",
  pracovni_rezim: "Pracovní režim", zdravotni: "Zdravotní omezení",
  soucasne_stravovani: "Současné stravování", leky: "Léky", poznamka: "Poznámka",
  poznamka_nutricni: "Poznámka nutriční terapeutky (závazná)",
};
const DOT_POLE = ["alergie", "vyloucit", "navyky"];
// Vlastní velké pole – nevykresluje se v mřížce dotazníku (má samostatný krok).
const DOT_MIMO_MRIZKU = ["poznamka_nutricni"];

const STAV_BADGE = {
  koncept: "text-slate-600 bg-slate-100 border-slate-200",
  generuje: "text-brand-700 bg-brand-50 border-brand-200",
  hotovo: "text-emerald-700 bg-emerald-50 border-emerald-200",
  chyba: "text-rose-700 bg-rose-50 border-rose-200",
};
const STAV_LABEL = { koncept: "koncept", generuje: "generuje se…", hotovo: "vygenerováno", chyba: "chyba" };

// ── Editor hotového dokumentu (7.8.2026) ────────────────────────────────────
// Nutriční potřebuje po vygenerování doladit maličkosti (překlep v názvu, jiná
// gramáž, výměna jedné suroviny). Přegenerovat kvůli tomu celý měsíc je drahé
// a trvá minuty, proto se hotová `data` upravují přímo a ukládají akcí
// "ulozit-data". Tvar dat se nemění — tiskový náhled (buildPdfHtml) čte úplně
// stejné klíče jako po generování.

const sklonJidlo = (n) => (Number(n) === 1 ? "jídle" : "jídlech");

// Kalorie držíme dopočtené z maker a zaokrouhlené na 5 přesně jako generátor
// (srovnejRecept v pdf-jidelnicek-generate-background.mjs) — jinak by v jednom
// dokumentu vedle sebe seděly dvě různé konvence.
const kcalZMaker5 = (b, s, t) =>
  Math.round((4 * (Number(b) || 0) + 4 * (Number(s) || 0) + 9 * (Number(t) || 0)) / 5) * 5;

// Porovnání bez diakritiky a velikosti písmen — surovinu píše nutriční volně
// („Protein" / „proteín"). Stejná normalizace jako na serveru.
const normSur = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const cisloZ = (v) => Math.max(0, Math.round(Number(v) || 0));

// Vrátí novou kopii dat s upraveným receptem na dané cestě. Kopírují se jen
// větve na cestě k jídlu — u 28 dní × chody by hluboká kopie při každém stisku
// klávesy zbytečně sekala psaní.
function nastavRecept(data, cesta, patch) {
  if (!cesta) return data;
  if (cesta.typ === "bonus") {
    return {
      ...data,
      bonusoveRecepty: (data.bonusoveRecepty || []).map((b, bi) => (bi !== cesta.bi ? b : {
        ...b,
        recepty: (b.recepty || []).map((r, ri) => (ri !== cesta.ri ? r : { ...r, ...patch })),
      })),
    };
  }
  return {
    ...data,
    tydny: (data.tydny || []).map((t, ti) => (ti !== cesta.ti ? t : {
      ...t,
      dny: (t.dny || []).map((den, di) => (di !== cesta.di ? den : {
        ...den,
        chody: (den.chody || []).map((ch, ci) => (ci !== cesta.ci ? ch : { ...ch, recept: { ...(ch.recept || {}), ...patch } })),
      })),
    })),
  };
}

// Projde VŠECHNA jídla dokumentu — týdny i zásobu bonusových receptů (i ta se
// klientce tiskne, takže vyměněná surovina v ní nesmí zůstat) — a vrátí ta,
// která hledanou surovinu obsahují, i s cestou pro zpětné dosazení.
function najdiJidlaSeSurovinou(data, hledane) {
  const q = normSur(hledane).trim();
  const nalezena = [];
  if (!q) return nalezena;
  const pridej = (r, cesta, kde, chod) => {
    const suroviny = Array.isArray(r?.suroviny) ? r.suroviny : [];
    if (!suroviny.some((s) => obsahujeSurovinu(s, hledane))) return;
    nalezena.push({
      cesta, kde, chod,
      nazev: r.nazev || "", suroviny, postup: r.postup || "",
      kcal: cisloZ(r.kcal), b: cisloZ(r.b), s: cisloZ(r.s), t: cisloZ(r.t),
    });
  };
  (data?.tydny || []).forEach((t, ti) => {
    (t.dny || []).forEach((den, di) => {
      (den.chody || []).forEach((ch, ci) => {
        pridej(ch.recept || {}, { typ: "tyden", ti, di, ci }, `${t.cislo ? `Týden ${t.cislo}` : `Týden ${ti + 1}`}, ${den.den || `den ${di + 1}`}`, ch.nazev || "");
      });
    });
  });
  (data?.bonusoveRecepty || []).forEach((b, bi) => {
    (b.recepty || []).forEach((r, ri) => {
      pridej(r || {}, { typ: "bonus", bi, ri }, "Zásoba receptů", b.chod || "");
    });
  });
  return nalezena;
}

// Před uložením: prázdné řádky surovin (vznikají psaním v textarea) by se
// v tisku vykreslily jako prázdné bublinky.
function ocistiData(data) {
  const ocistiRecept = (r) => ({
    ...r,
    suroviny: (Array.isArray(r?.suroviny) ? r.suroviny : []).map((s) => String(s).trim()).filter(Boolean),
  });
  return {
    ...data,
    tydny: (data.tydny || []).map((t) => ({
      ...t,
      dny: (t.dny || []).map((den) => ({
        ...den,
        chody: (den.chody || []).map((ch) => ({ ...ch, recept: ocistiRecept(ch.recept || {}) })),
      })),
    })),
    bonusoveRecepty: (data.bonusoveRecepty || []).map((b) => ({
      ...b,
      recepty: (b.recepty || []).map(ocistiRecept),
    })),
  };
}

// Jedno editovatelné jídlo. Bez vlastního stavu — hodnoty i změny drží editor,
// aby se rozpracované úpravy neztratily sbalením sekce.
function EditorJidla({ stitek, recept, onZmena, konflikt, onNahradit, nahradaBezi }) {
  const r = recept || {};
  const zmenMakro = (klic, hodnota) => {
    const nova = { b: cisloZ(r.b), s: cisloZ(r.s), t: cisloZ(r.t), [klic]: cisloZ(hodnota) };
    onZmena({ [klic]: nova[klic], kcal: kcalZMaker5(nova.b, nova.s, nova.t) });
  };
  const poleMakro = (klic, popis) => (
    <label key={klic} className="block">
      <span className="block text-[11px] font-semibold text-slate-500 mb-0.5">{popis}</span>
      <input type="number" min="0" value={cisloZ(r[klic])} onChange={(e) => zmenMakro(klic, e.target.value)}
        className="field w-20" />
    </label>
  );
  return (
    <div className={`border rounded-xl p-3 space-y-2 bg-white ${konflikt ? "border-rose-400 ring-1 ring-rose-100" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-wide text-plum-700 bg-plum-50 border border-plum-200 rounded-full px-2.5 py-0.5">{stitek}</span>
        <input value={r.nazev || ""} onChange={(e) => onZmena({ nazev: e.target.value })}
          placeholder="Název jídla"
          className="field flex-1 min-w-[180px] font-semibold" />
        {onNahradit && (
          <button type="button" onClick={onNahradit} disabled={!!nahradaBezi}
            title="Nabídne 3 náhrady z databáze ověřených receptů podle aktuálního dotazníku tohoto dokumentu (respektuje alergie a zákazy)."
            className="text-[11px] font-semibold text-plum-700 border border-plum-300 bg-plum-50 hover:bg-plum-100 rounded-lg px-2.5 py-1.5 whitespace-nowrap disabled:opacity-60 disabled:cursor-wait">
            {nahradaBezi ? "Hledám…" : "⇄ Nahradit z databáze"}
          </button>
        )}
      </div>
      {konflikt && (
        <div className="text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">
          ⚠ Koliduje s dotazníkem: {(konflikt.duvody || []).join("; ")}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2.5">
        <label className="block">
          <span className="block text-[11px] font-semibold text-slate-500 mb-0.5">Kalorie</span>
          <input type="number" min="0" value={cisloZ(r.kcal)} onChange={(e) => onZmena({ kcal: cisloZ(e.target.value) })}
            className="field w-24 font-bold" />
        </label>
        {poleMakro("b", "Bílkoviny (g)")}
        {poleMakro("s", "Sacharidy (g)")}
        {poleMakro("t", "Tuky (g)")}
        <span className="text-[11px] text-slate-400 pb-2">Po změně maker se kalorie dopočítají (4×B + 4×S + 9×T).</span>
      </div>
      <label className="block">
        <span className="block text-[11px] font-semibold text-slate-500 mb-0.5">Suroviny (jedna na řádek)</span>
        <textarea rows={Math.min(12, Math.max(3, (r.suroviny || []).length + 1))}
          value={(Array.isArray(r.suroviny) ? r.suroviny : []).join("\n")}
          onChange={(e) => onZmena({ suroviny: e.target.value.split("\n") })}
          placeholder={"2 lžíce (30 g) ovesné vločky\n1 ks (60 g) vejce"}
          className="field w-full font-mono leading-relaxed" />
      </label>
      <label className="block">
        <span className="block text-[11px] font-semibold text-slate-500 mb-0.5">Postup</span>
        <textarea rows={3} value={r.postup || ""} onChange={(e) => onZmena({ postup: e.target.value })}
          className="field w-full leading-relaxed" />
      </label>
    </div>
  );
}

function PdfEditor({ dok, onZavrit, onUlozeno }) {
  const [data, setData] = useState(() => dok.data || {});
  const [neulozeno, setNeulozeno] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  // Sbalené sekce: 28 dní × chody by jinak bylo nepřehledné. První týden
  // otevřený, ať je hned vidět, že editor něco obsahuje. Týdny a dny
  // s kolizemi dotazníku se otevřou rovnou (10.9.2026).
  const [otevreneTydny, setOtevreneTydny] = useState(() => {
    const o = { 0: true };
    (dok._konflikty || []).forEach((k) => { if (k.typ === "tyden") o[k.ti] = true; });
    return o;
  });
  const [otevreneDny, setOtevreneDny] = useState(() => {
    const o = {};
    (dok._konflikty || []).forEach((k) => { if (k.typ === "tyden") o[`${k.ti}-${k.di}`] = true; });
    return o;
  });
  const [otevrenyBonus, setOtevrenyBonus] = useState(() => {
    const o = {};
    (dok._konflikty || []).forEach((k) => { if (k.typ === "bonus") o[k.bi] = true; });
    return o;
  });
  // Kolize hotového jídelníčku s aktuálním dotazníkem (10.9.2026) — spočítané
  // serverem při otevření editoru. Náhrada z databáze kolizi řeší (recepty
  // z nabídky už zákazy prošly), po použití náhrady štítek zmizí.
  const [konflikty, setKonflikty] = useState(() => dok._konflikty || []);
  // Nabídka náhrad z databáze pro jedno jídlo: { klic, cesta, chod, recepty }.
  const [nabidka, setNabidka] = useState(null);
  const [nahradaBusy, setNahradaBusy] = useState("");
  const cestaKlic = (c) => (c.typ === "bonus" ? `b-${c.bi}-${c.ri}` : `t-${c.ti}-${c.di}-${c.ci}`);
  const konfliktMapa = useMemo(() => {
    const m = {};
    for (const k of konflikty) m[cestaKlic(k)] = k;
    return m;
  }, [konflikty]);

  const nahraditZDb = async (cesta, chodNazev, recept) => {
    const klic = cestaKlic(cesta);
    setNahradaBusy(klic); setErr("");
    try {
      const r = await apiFetch("/api/pdf-jidelnicek", {
        method: "POST",
        body: JSON.stringify({
          action: "nahradit-recept-pdf", id: dok.id, chod: chodNazev || "",
          cilKcal: cisloZ(recept?.kcal) || 400,
          // Nenabízet totéž jídlo ani recepty z minulé nabídky u stejného jídla.
          vynechat: [recept?.nazev, ...((nabidka && nabidka.klic === klic ? nabidka.recepty : []).map((x) => x.nazev))].filter(Boolean),
        }),
      });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Náhrada selhala"); return; }
      setNabidka({ klic, cesta, chod: chodNazev || "", recepty: Array.isArray(j.recepty) ? j.recepty : [] });
    } catch (e) { setErr(e.message || "Náhrada selhala"); }
    finally { setNahradaBusy(""); }
  };

  const pouzitNahradu = (r) => {
    if (!nabidka) return;
    uprav(nabidka.cesta, {
      nazev: r.nazev, suroviny: r.suroviny, postup: r.postup,
      kcal: cisloZ(r.kcal), b: cisloZ(r.b), s: cisloZ(r.s), t: cisloZ(r.t),
    });
    setKonflikty((ks) => ks.filter((k) => cestaKlic(k) !== nabidka.klic));
    setNabidka(null);
  };

  // Panel s nabídkou 3 náhrad pod jídlem, kterého se týká.
  const nabidkaPanel = (klic) => (nabidka && nabidka.klic === klic ? (
    <div className="rounded-xl border border-plum-300 bg-plum-50/70 p-2.5 space-y-2">
      <div className="text-[11px] font-semibold text-plum-800">
        Náhrady z databáze receptů, respektují aktuální dotazník. Kliknutím jídlo nahradíte (do tisku až po „Uložit úpravy").
      </div>
      {nabidka.recepty.map((r, i) => (
        <button key={i} type="button" onClick={() => pouzitNahradu(r)}
          className="w-full text-left bg-white border border-plum-200 hover:border-plum-400 rounded-lg px-3 py-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs font-bold text-slate-800">{r.nazev}</span>
            <span className="text-[11px] text-slate-500 tabular-nums">{r.kcal} kcal, B {r.b}, S {r.s}, T {r.t}</span>
          </div>
          <div className="text-[11px] text-slate-500 truncate">{(r.suroviny || []).join(", ")}</div>
        </button>
      ))}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => nahraditZDb(nabidka.cesta, nabidka.chod, { nazev: "", kcal: (nabidka.recepty[0] || {}).kcal })}
          disabled={!!nahradaBusy}
          className="text-[11px] font-semibold text-plum-700 border border-plum-300 bg-white hover:bg-plum-100 rounded-lg px-2.5 py-1.5 disabled:opacity-60">
          {nahradaBusy ? "Hledám…" : "↺ Jiné návrhy"}
        </button>
        <button type="button" onClick={() => setNabidka(null)}
          className="text-[11px] text-slate-500 hover:text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5">
          Zavřít nabídku
        </button>
      </div>
    </div>
  ) : null);
  const [surZ, setSurZ] = useState("");
  const [surNa, setSurNa] = useState("");
  const [surNalez, setSurNalez] = useState(null);
  const [surZprava, setSurZprava] = useState("");
  const [surBusy, setSurBusy] = useState(false);

  const tydny = data.tydny || [];
  const bonusy = data.bonusoveRecepty || [];
  const pocetJidel = useMemo(
    () => tydny.reduce((a, t) => a + (t.dny || []).reduce((x, den) => x + (den.chody || []).length, 0), 0),
    [tydny]
  );

  const uprav = (cesta, patch) => {
    setNeulozeno(true);
    setInfo("");
    setData((d) => nastavRecept(d, cesta, patch));
  };

  const ulozit = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const r = await apiFetch("/api/pdf-jidelnicek", {
        method: "POST",
        body: JSON.stringify({ action: "ulozit-data", id: dok.id, data: ocistiData(data) }),
      });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Uložení selhalo"); }
      else {
        setNeulozeno(false);
        setInfo("Úpravy jsou uložené. Tiskový náhled je teď vytiskne i s nimi.");
        if (onUlozeno) onUlozeno();
      }
    } catch (e) { setErr(e.message || "Uložení selhalo"); }
    setBusy(false);
  };

  // Krok 1 — zásah se spočítá LOKÁLNĚ z rozpracovaných dat. AI se zatím
  // nevolá, nutriční nejdřív uvidí, kolika jídel se to týká (a odchytí překlep).
  const najdiSurovinu = () => {
    setErr(""); setInfo(""); setSurNalez(null);
    const z = surZ.trim();
    const na = surNa.trim();
    if (!z || !na) { setSurZprava("Vyplňte obě pole: kterou surovinu měnit a čím ji nahradit."); return; }
    const nalezena = najdiJidlaSeSurovinou(data, z);
    if (!nalezena.length) {
      setSurZprava(`Surovinu „${z}" jsme v tomhle jídelníčku nenašli, zkontrolujte překlep. AI jsme nevolali.`);
      return;
    }
    // Server bere maximálně 120 jídel na jedno volání; zbytek se dá zaměnit
    // druhým průchodem (po uložení už se najdou jen ta neupravená).
    const davka = nalezena.slice(0, 120);
    setSurZprava(`Našli jsme ji v ${nalezena.length} ${sklonJidlo(nalezena.length)}.${nalezena.length > davka.length ? ` Naráz zvládneme ${davka.length}, po uložení spusťte záměnu ještě jednou.` : ""}`);
    setSurNalez({ z, na, jidla: davka, celkem: nalezena.length });
  };

  // Krok 2 — potvrzeno: AI přepočítá jen zasažená jídla a výsledek jde do
  // rozpracovaných dat (ne do DB) — uloží se až tlačítkem „Uložit úpravy".
  const spustZamenu = async () => {
    if (!surNalez || surBusy) return;
    setSurBusy(true); setErr("");
    try {
      const r = await apiFetch("/api/jidelnicek", {
        method: "POST",
        body: JSON.stringify({
          action: "zamenit-surovinu", klient_id: dok.klient_id, z: surNalez.z, na: surNalez.na,
          jidla: surNalez.jidla.map((x) => ({
            nazev: x.nazev, suroviny: x.suroviny, postup: x.postup,
            kcal: x.kcal, b: x.b, s: x.s, t: x.t,
          })),
        }),
      });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Záměna selhala"); setSurBusy(false); return; }
      const vysledky = Array.isArray(j.jidla) ? j.jidla : [];
      if (!vysledky.length) { setErr("AI nevrátila žádné upravené jídlo, zkuste to znovu."); setSurBusy(false); return; }
      setData((d) => vysledky.reduce((acc, vys) => {
        const cil = surNalez.jidla[vys.index];
        if (!cil) return acc;
        return nastavRecept(acc, cil.cesta, {
          nazev: vys.nazev || cil.nazev,
          suroviny: Array.isArray(vys.suroviny) && vys.suroviny.length ? vys.suroviny : cil.suroviny,
          postup: vys.postup || cil.postup,
          kcal: cisloZ(vys.kcal), b: cisloZ(vys.b), s: cisloZ(vys.s), t: cisloZ(vys.t),
        });
      }, d));
      setNeulozeno(true);
      setSurZprava(`Zaměněno v ${vysledky.length} ${sklonJidlo(vysledky.length)}. Zkontrolujte to a uložte tlačítkem „Uložit úpravy", teprve pak to bude v tisku.`);
      setSurNalez(null);
    } catch (e) { setErr(e.message || "Záměna selhala"); }
    setSurBusy(false);
  };

  const prepniTyden = (ti) => setOtevreneTydny((p) => ({ ...p, [ti]: !p[ti] }));
  const prepniDen = (klic) => setOtevreneDny((p) => ({ ...p, [klic]: !p[klic] }));
  const prepniBonus = (bi) => setOtevrenyBonus((p) => ({ ...p, [bi]: !p[bi] }));

  return (
    <div className="space-y-4">
      <div className="bg-white border-2 border-plum-300 rounded-2xl shadow-sm p-4 space-y-3">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-plum-800 flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Úprava jídelníčku: {dok.klient_name}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {tydny.length} {tydny.length === 1 ? "týden" : tydny.length < 5 ? "týdny" : "týdnů"}
              <span aria-hidden className="inline-block align-middle w-px h-3 bg-current opacity-40 mx-1.5" />
              {pocetJidel} jídel
              <span aria-hidden className="inline-block align-middle w-px h-3 bg-current opacity-40 mx-1.5" />
              vygenerováno {fmtDT(dok.updated_at)}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={ulozit} disabled={busy || !neulozeno}
              className="btn btn-primary">
              <Save className="w-4 h-4" /> {busy ? "Ukládám…" : "Uložit úpravy"}
            </button>
            <button onClick={() => onZavrit(neulozeno)} disabled={busy}
              className="btn btn-secondary">
              Zavřít editor
            </button>
          </div>
        </div>
        {neulozeno && (
          <div className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
            ⚠️ Máte neuložené úpravy. Dokud nekliknete na „Uložit úpravy", zůstávají jen tady v okně, po zavření nebo obnovení stránky se ztratí.
          </div>
        )}
        {info && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">{info}</div>}
        {err && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">{err}</div>}
        <div className="text-[11px] text-slate-400">
          Úpravy se propíšou do tiskového náhledu. Týdenní nákupní seznamy a bonusové kapitoly se nepřepočítávají,
          když měníte gramáže ve velkém, projděte prosím i nákupní seznam.
        </div>
        {konflikty.length > 0 && (
          <div className="text-xs font-semibold text-rose-800 bg-rose-50 border border-rose-300 rounded-xl px-3 py-2">
            ⚠ Jídelníček koliduje s aktuálním dotazníkem v {konflikty.length} {sklonJidlo(konflikty.length)}, jsou zvýrazněná
            červeně (týdny a dny s kolizí jsou rovnou rozbalené). Nejrychlejší oprava: „⇄ Nahradit z databáze" přímo u jídla.
          </div>
        )}
      </div>

      {/* 🔁 Hromadná záměna suroviny — nejdřív se zásah spočítá lokálně,
          teprve po potvrzení jde AI (a jen se zasaženými jídly). */}
      <div className="bg-white border border-plum-200 rounded-xl p-3.5 space-y-2.5">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wide text-plum-700">🔁 Zaměnit surovinu</span>
          <span className="text-[11px] text-slate-400">Vymění jednu surovinu ve všech jídlech i v zásobě receptů, bez generování celého jídelníčku.</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="text" value={surZ}
            onChange={(e) => { setSurZ(e.target.value); setSurNalez(null); setSurZprava(""); }}
            placeholder="Co vyměnit, např. rostlinný protein"
            className="field flex-1 min-w-[180px]" />
          <span aria-hidden className="text-sm font-bold text-plum-300">→</span>
          <input type="text" value={surNa}
            onChange={(e) => { setSurNa(e.target.value); setSurNalez(null); setSurZprava(""); }}
            placeholder="Čím nahradit, např. syrovátkový protein"
            className="field flex-1 min-w-[180px]" />
          <button type="button" onClick={najdiSurovinu} disabled={surBusy}
            title="Spočítá, kolika jídel se záměna týká. AI se volá až po potvrzení."
            className="btn btn-primary whitespace-nowrap">
            <RefreshCw className="w-3.5 h-3.5" /> Najít a zaměnit
          </button>
        </div>
        {surZprava && <div className="text-[11px] font-semibold text-plum-700">{surZprava}</div>}
        {surNalez && (
          <div className="rounded-xl border border-plum-200 bg-plum-50/70 p-2.5 space-y-2">
            <div className="text-xs text-slate-700">
              Zaměníme <strong>{surNalez.z}</strong> → <strong>{surNalez.na}</strong> v {surNalez.jidla.length} {sklonJidlo(surNalez.jidla.length)}.
              AI dopočítá novou gramáž i makra, aby jídla zůstala na svých kcal (±10 %). Zbytek surovin zůstane beze změny.
            </div>
            <ul className="space-y-0.5 max-h-40 overflow-auto">
              {surNalez.jidla.map((x, xi) => (
                <li key={xi} className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-500">
                  <span className="font-medium text-slate-700">{x.nazev || "(bez názvu)"}</span>
                  {x.kde ? (<><span aria-hidden className="w-px h-3 bg-current opacity-40" />{x.kde}</>) : null}
                  {x.chod ? (<><span aria-hidden className="w-px h-3 bg-current opacity-40" />{x.chod}</>) : null}
                  <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                  {x.kcal || "?"} kcal
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={spustZamenu} disabled={surBusy}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-r from-brand-600 to-plum-600 hover:from-brand-700 hover:to-plum-700 rounded-xl px-3 py-2 shadow-sm disabled:opacity-60 min-h-[44px] sm:min-h-0">
                <Sparkles className="w-3.5 h-3.5" />
                {surBusy ? "AI přepočítává jídla…" : `Zaměnit v ${surNalez.jidla.length} ${sklonJidlo(surNalez.jidla.length)} ✓`}
              </button>
              <button type="button" onClick={() => { setSurNalez(null); setSurZprava(""); }} disabled={surBusy}
                className="text-xs text-slate-500 hover:text-slate-700 border border-slate-300 rounded-xl px-3 py-2 disabled:opacity-60">
                Zrušit
              </button>
              <span className="text-[11px] text-slate-400">Změna jde jen do rozpracovaného dokumentu, do tisku až po „Uložit úpravy".</span>
            </div>
          </div>
        )}
      </div>

      {/* Týdny → dny → jídla */}
      {tydny.map((t, ti) => {
        const otevreny = !!otevreneTydny[ti];
        const dny = t.dny || [];
        return (
          <div key={ti} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <button type="button" onClick={() => prepniTyden(ti)}
              className="w-full flex items-center gap-2 px-3.5 py-3 text-left hover:bg-slate-50">
              <span className="text-slate-400 text-xs w-4">{otevreny ? "▾" : "▸"}</span>
              <span className="text-sm font-bold text-slate-800">Týden {t.cislo || ti + 1}</span>
              <span className="text-[11px] text-slate-400">
                {dny.length} {dny.length === 1 ? "den" : dny.length < 5 ? "dny" : "dní"}
              </span>
            </button>
            {otevreny && (
              <div className="px-3.5 pb-3.5 space-y-2">
                {dny.map((den, di) => {
                  const klic = `${ti}-${di}`;
                  const denOtevreny = !!otevreneDny[klic];
                  const chody = den.chody || [];
                  const soucet = chody.reduce((a, ch) => a + cisloZ(ch.recept?.kcal), 0);
                  return (
                    <div key={di} className="border border-slate-200 rounded-xl overflow-hidden">
                      <button type="button" onClick={() => prepniDen(klic)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-plum-50/50">
                        <span className="text-slate-400 text-xs w-4">{denOtevreny ? "▾" : "▸"}</span>
                        <span className="text-sm font-semibold text-slate-700">{den.den || `Den ${di + 1}`}</span>
                        <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
                          {chody.length} {sklonChod(chody.length)}
                          <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                          ≈ {soucet} kcal
                        </span>
                      </button>
                      {denOtevreny && (
                        <div className="p-2.5 space-y-2.5 bg-slate-50/60">
                          {chody.map((ch, ci) => {
                            const klic = `t-${ti}-${di}-${ci}`;
                            return (
                              <div key={ci} className="space-y-2">
                                <EditorJidla stitek={ch.nazev || `Chod ${ci + 1}`} recept={ch.recept || {}}
                                  onZmena={(patch) => uprav({ typ: "tyden", ti, di, ci }, patch)}
                                  konflikt={konfliktMapa[klic]}
                                  nahradaBezi={nahradaBusy === klic}
                                  onNahradit={() => nahraditZDb({ typ: "tyden", ti, di, ci }, ch.nazev || "", ch.recept)} />
                                {nabidkaPanel(klic)}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Zásoba bonusových receptů — tiskne se klientce taky, takže musí jít upravit. */}
      {bonusy.map((b, bi) => {
        const otevreny = !!otevrenyBonus[bi];
        const recepty = b.recepty || [];
        return (
          <div key={bi} className="bg-white border border-amber-200 rounded-2xl overflow-hidden">
            <button type="button" onClick={() => prepniBonus(bi)}
              className="w-full flex items-center gap-2 px-3.5 py-3 text-left hover:bg-amber-50/60">
              <span className="text-slate-400 text-xs w-4">{otevreny ? "▾" : "▸"}</span>
              <span className="text-sm font-bold text-amber-800">★ Zásoba receptů: {b.chod}</span>
              <span className="text-[11px] text-slate-400">{recepty.length} receptů</span>
            </button>
            {otevreny && (
              <div className="p-2.5 space-y-2.5 bg-amber-50/40">
                {recepty.map((r, ri) => {
                  const klic = `b-${bi}-${ri}`;
                  return (
                    <div key={ri} className="space-y-2">
                      <EditorJidla stitek={b.chod || "Bonus"} recept={r}
                        onZmena={(patch) => uprav({ typ: "bonus", bi, ri }, patch)}
                        konflikt={konfliktMapa[klic]}
                        nahradaBezi={nahradaBusy === klic}
                        onNahradit={() => nahraditZDb({ typ: "bonus", bi, ri }, b.chod || "", r)} />
                      {nabidkaPanel(klic)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex items-center gap-2 flex-wrap pb-2">
        <button onClick={ulozit} disabled={busy || !neulozeno}
          className="btn btn-primary">
          <Save className="w-4 h-4" /> {busy ? "Ukládám…" : "Uložit úpravy"}
        </button>
        <button onClick={() => onZavrit(neulozeno)} disabled={busy}
          className="btn btn-secondary">
          Zrušit a zavřít editor
        </button>
      </div>
    </div>
  );
}

export default function PdfJidelnicky() {
  const user = getCurrentUser();
  const role = user?.role || "";
  const isOwner = role === "owner";

  const [seznam, setSeznam] = useState(null);
  // Naposledy použité znění upozornění — předvyplní se jím Krok 4 (7.9.2026).
  const [vychoziUpozorneni, setVychoziUpozorneni] = useState("");
  // Úprava upozornění u už vygenerovaného dokumentu: { id, jmeno, text, zap }.
  const [upozEdit, setUpozEdit] = useState(null);
  const [celkem, setCelkem] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hledatDoc, setHledatDoc] = useState("");
  const [klienti, setKlienti] = useState([]);
  const [klientId, setKlientId] = useState("");
  const [hledat, setHledat] = useState("");
  const [rezim, setRezim] = useState("klientka"); // "klientka" | "jednorazovy"
  const [jJmeno, setJJmeno] = useState("");
  const [jText, setJText] = useState("");
  const [jPdf, setJPdf] = useState(null); // {name, b64} — nahraný PDF dotazník
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [kontrola, setKontrola] = useState(null); // {id, ans} — kontrola dotazníku před generováním
  const [editor, setEditor] = useState(null); // celý dokument (včetně dat) otevřený v editoru
  // Korektura češtiny nad hotovým dokumentem (7.8.2026): id dokumentu, nad
  // kterým právě běží, a výsledek poslední korektury { id, text }.
  const [korBusy, setKorBusy] = useState(null);
  const [korVysledek, setKorVysledek] = useState(null);
  const [err, setErr] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const pollRef = useRef(null);

  const load = async (off = offset, q = hledatDoc) => {
    try {
      const r = await apiFetch(`/api/pdf-jidelnicek?offset=${off}&hledat=${encodeURIComponent(q)}`);
      const j = await r.json();
      if (j.ok) { setSeznam(j.seznam || []); setCelkem(j.celkem || 0); setVychoziUpozorneni(j.vychoziUpozorneni || ""); }
    } catch (_) { /* ticho */ }
  };
  useEffect(() => { load(offset, hledatDoc); }, [offset, hledatDoc]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    apiFetch("/api/clients").then((r) => r.json()).then((j) => {
      const arr = (j.clients || []).filter((c) => c.klient && (c.notion || c.notion_id));
      setKlienti(arr);
    }).catch(() => {});
  }, []);

  // Poll dokud se něco generuje.
  const generujeSe = (seznam || []).some((d) => d.stav === "generuje");
  useEffect(() => {
    if (!generujeSe) { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(load, 5000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [generujeSe]); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (payload) => {
    setErr("");
    const r = await apiFetch("/api/pdf-jidelnicek", { method: "POST", body: JSON.stringify(payload) });
    const j = await r.json();
    if (!j.ok) { setErr(j.error || "Akce selhala"); return null; }
    return j;
  };

  const vytvorit = async () => {
    setInfo("");
    setBusy(true);
    let c = null;
    if (rezim === "jednorazovy") {
      if (jJmeno.trim().length < 3) { setErr("Zadejte jméno zákazníka"); setBusy(false); return; }
      if (jText.trim().length < 30 && !jPdf) { setErr("Vložte odpovědi zákazníka (text nebo PDF)"); setBusy(false); return; }
      c = await post({ action: "create-jednorazovy", jmeno: jJmeno.trim(), dotaznik_text: jText, dotaznik_pdf: jPdf ? jPdf.b64 : undefined });
      if (c && c.id) {
        if (c.varovani) setInfo(c.varovani);
        // Kontrolní krok: ukázat návrh příjmu + vytažený dotazník, generuje se až po potvrzení.
        setKontrola({ id: c.id, ans: dotaznikNaFormular(c.dotaznik || {}), pr: prijemNaFormular(c.prijem), vypocet: c.prijem || null, ...vychoziUpoz(c.dotaznik) });
        setJJmeno(""); setJText(""); setJPdf(null);
        await load();
      }
      setBusy(false);
      return;
    } else {
      if (!klientId) { setErr("Vyberte klientku"); setBusy(false); return; }
      c = await post({ action: "create", klient_id: klientId });
    }
    // Krok 1 (15.7.2026): i u klientky z aplikace se NEgeneruje rovnou.
    // Nejdřív se ukáže návrh kalorického příjmu a rozložení živin + dotazník,
    // nutriční je upraví / uloží jako koncept / rovnou vygeneruje.
    if (c && c.id) {
      if (c.varovani) setInfo(c.varovani);
      setKontrola({ id: c.id, ans: dotaznikNaFormular(c.dotaznik || {}), pr: prijemNaFormular(c.prijem), vypocet: c.prijem || null, ...vychoziUpoz(c.dotaznik) });
      setKlientId(""); setHledat("");
      await load();
    }
    setBusy(false);
  };

  const dotaznikNaFormular = (d) => {
    const init = {};
    Object.keys(DOT_LABELS).forEach((k) => {
      const v = d[k];
      init[k] = Array.isArray(v) ? v.join(", ") : (v == null ? "" : String(v));
    });
    return init;
  };

  // ── Návrh příjmu (15.7.2026): krok 1 před generováním ──
  // Výpočet a formulářové helpery sdílené v src/lib/prijem.js (používá je
  // i JidelnicekSection pro jídelníčky do aplikace).
  const zmenMakro = (k, v) => setKontrola((p) => {
    const pr = { ...p.pr, [k]: v };
    pr.kalorie = String(kcalZMaker(pr) || "");
    return { ...p, pr };
  });
  // Kcal se přepisuje volně, makra se doškálují až při opuštění pole
  // (jinak by průběžné psaní ničilo poměry).
  const zmenKcalBlur = () => setKontrola((p) => {
    const cil = parseInt(p.pr.kalorie, 10) || 0;
    const cur = kcalZMaker(p.pr);
    if (!cil || !cur) return p;
    const f = cil / cur;
    const n = (x) => String(Math.round((parseInt(p.pr[x], 10) || 0) * f));
    const pr = { kalorie: p.pr.kalorie, bilkoviny: n("bilkoviny"), sacharidy: n("sacharidy"), tuky: n("tuky") };
    pr.kalorie = String(kcalZMaker(pr) || "");
    return { ...p, pr };
  });
  const prepocitatZDotazniku = () => setKontrola((p) => {
    const v = spocitejPrijemJs(p.ans);
    if (!v) { setErr("Pro výpočet chybí věk, výška nebo váha v dotazníku."); return p; }
    setErr("");
    return { ...p, pr: prijemNaFormular(v), vypocet: v };
  });
  const prijemPayload = () => {
    if (!kontrola) return null;
    const n = (x) => parseInt(String(kontrola.pr[x] || "").trim(), 10) || 0;
    const b = n("bilkoviny"), s = n("sacharidy"), t = n("tuky");
    return b && s && t ? { bilkoviny: b, sacharidy: s, tuky: t } : null;
  };

  const otevriKontrolu = async (dok) => {
    setErr(""); setInfo("");
    const r = await apiFetch(`/api/pdf-jidelnicek?id=${encodeURIComponent(dok.id)}`);
    const j = await r.json();
    if (!j.ok) { setErr(j.error || "Dokument se nepodařilo načíst"); return; }
    // stav řídí režim formuláře: koncept = kontrola před generováním,
    // hotovo = úprava dotazníku vygenerovaného dokumentu (10.9.2026).
    setKontrola({ id: dok.id, stav: j.dokument.stav, jmeno: dok.klient_name || "", ans: dotaznikNaFormular(j.dokument.dotaznik || {}), pr: prijemNaFormular(j.dokument.prijem), vypocet: j.dokument.prijem || null, ...vychoziUpoz(j.dokument.dotaznik) });
    window.scrollTo({ top: 0 });
  };

  // Uložení dotazníku u VYGENEROVANÉHO dokumentu (10.9.2026): jídelníček se
  // nepřegenerovává, server vrátí kolize s novým dotazníkem.
  const ulozitDotaznikHotoveho = async () => {
    setBusy(true);
    const u = await ulozitKontrolu();
    if (u) {
      const n = (u.konflikty || []).length;
      let msg = n
        ? `Dotazník uložen. Jídelníček s ním koliduje v ${n} ${sklonJidlo(n)}, otevřete Upravit, kolizní jídla jsou zvýrazněná červeně a vyřešíte je tlačítkem „Nahradit z databáze".`
        : "Dotazník uložen. Jídelníček s novým dotazníkem nekoliduje.";
      const kcalNove = u.prijem && u.prijem.kalorie;
      if (kcalNove && u.kcalDokumentu && Math.abs(kcalNove - u.kcalDokumentu) / u.kcalDokumentu > 0.10) {
        msg += ` Pozor: nový denní příjem ${kcalNove} kcal se od vygenerovaného jídelníčku (${u.kcalDokumentu} kcal) liší o víc než 10 %, ruční výměny to nedoženou, zvažte nový dokument.`;
      }
      setInfo(msg);
      setKontrola(null);
      await load();
    }
    setBusy(false);
  };

  // Uloží dotazník + příjem do konceptu (bez generování / před ním).
  // Upozornění u hotového dokumentu — text se tiskne až při otevření náhledu,
  // takže stačí přepsat záznam, nic se negeneruje znovu.
  const otevritUpozorneni = async (dok) => {
    setErr("");
    const r = await apiFetch(`/api/pdf-jidelnicek?id=${encodeURIComponent(dok.id)}`);
    const j = await r.json();
    if (!j.ok) { setErr(j.error || "Načtení selhalo"); return; }
    const d = (j.dokument && j.dokument.dotaznik) || {};
    setUpozEdit({
      id: dok.id,
      jmeno: dok.klient_name || "",
      text: String(d._upozorneni || "") || String(vychoziUpozorneni || "") || UPOZORNENI_NAVRH,
      zap: d._upozorneni_zap !== false && !!String(d._upozorneni || "").trim(),
    });
  };

  const ulozitUpozorneni = async () => {
    if (!upozEdit) return;
    setBusy(true);
    const ok = await post({
      action: "upozorneni-ulozit", id: upozEdit.id,
      upozorneni: upozEdit.text, upozorneniZap: upozEdit.zap,
    });
    if (ok) {
      setUpozEdit(null);
      setInfo(upozEdit.zap
        ? "Upozornění uložené. Tiskový náhled ho teď vytiskne."
        : "Upozornění vypnuté. Do tiskového náhledu se nedostane.");
      await load();
    }
    setBusy(false);
  };

  // Přednost má text uložený u konceptu, pak naposledy použitý, pak návrh znění.
  // Přepínač je zapnutý, dokud ho někdo výslovně nevypne.
  const vychoziUpoz = (dotaznik) => {
    const d = dotaznik || {};
    const ulozeny = String(d._upozorneni || "").trim();
    return {
      upoz: ulozeny || String(vychoziUpozorneni || "") || UPOZORNENI_NAVRH,
      upozZap: d._upozorneni_zap !== false,
    };
  };

  const ulozitKontrolu = async () => {
    if (!kontrola) return null;
    const answers = {};
    Object.keys(DOT_LABELS).forEach((k) => {
      const txt = String(kontrola.ans[k] == null ? "" : kontrola.ans[k]).trim();
      if (!txt) return;
      answers[k] = DOT_POLE.includes(k) ? txt.split(",").map((x) => x.trim()).filter(Boolean) : txt;
    });
    return post({
      action: "dotaznik-upravit", id: kontrola.id, answers, prijem: prijemPayload(),
      // Upozornění není odpověď z dotazníku, jde vedle — do PDF, ne do generátoru.
      upozorneni: kontrola.upoz || "",
      upozorneniZap: !!kontrola.upozZap,
    });
  };

  const ulozitKoncept = async () => {
    setBusy(true);
    const u = await ulozitKontrolu();
    if (u) { setInfo("Uloženo jako koncept. Najdete ho v historii, generování spustíte kdykoli."); setKontrola(null); await load(); }
    setBusy(false);
  };

  const potvrditAGenerovat = async () => {
    if (!kontrola) return;
    setBusy(true);
    const u = await ulozitKontrolu();
    if (u) {
      if (!u.prijem) setInfo("Pozor: bez příjmu AI odhadne kalorie jen zhruba.");
      if (await post({ action: "generate", id: kontrola.id })) {
        setKontrola(null);
        await load();
      }
    }
    setBusy(false);
  };

  const nactiSoubor = (file) => {
    if (!file) return;
    if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
      if (file.size > 3 * 1024 * 1024) { setErr("PDF je moc velké (max 3 MB)"); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = String(reader.result || "").replace(/^data:application\/pdf;base64,/, "");
        setJPdf({ name: file.name, b64 });
      };
      reader.readAsDataURL(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setJText(String(reader.result || "").slice(0, 12000));
    reader.readAsText(file, "utf-8");
  };

  const smazat = (dok) => {
    setConfirmState({
      title: dok.stav === "hotovo" ? "Smazat vygenerovaný dokument?" : "Smazat koncept?",
      description: <>Dokument pro <strong>{dok.klient_name}</strong> z {fmtDT(dok.created_at)} se nenávratně smaže.</>,
      confirmLabel: "Smazat",
      onConfirm: async () => {
        setConfirmState(null);
        if (await post({ action: "smazat", id: dok.id })) await load();
      },
    });
  };

  // ── Korektura češtiny nad HOTOVÝM dokumentem (7.8.2026) ─────────────────
  // Korektor jinak běží jen při generování, takže dřív vygenerované dokumenty
  // si své chyby („cukankového", „opečete") nesou v DB napořád. Server je
  // opraví a rovnou uloží — proto po doběhnutí obnovujeme seznam.

  // České skloňování počtu (1 výraz / 2 výrazy / 5 výrazů).
  const pocetSlovem = (n, [jeden, dva, pet]) => `${n} ${n === 1 ? jeden : n >= 2 && n <= 4 ? dva : pet}`;

  // Statistika z korektoru → věta pro nutriční.
  const korekturaZprava = (stat) => {
    const s = stat || {};
    const opraveno = (s.slovnik || 0) + (s.ai || 0);
    const casti = [];
    if (opraveno) casti.push(`Opraveno ${pocetSlovem(opraveno, ["výraz", "výrazy", "výrazů"])}`);
    if (s.sloh) casti.push(pocetSlovem(s.sloh, ["slohová úprava", "slohové úpravy", "slohových úprav"]));
    if (!casti.length) return `Čeština je v pořádku, nic k opravě.${s.chyba ? ` (AI korektura selhala: ${s.chyba})` : ""}`;
    let txt = `${casti.join(", ")}.`;
    if (s.zbyva) txt += ` Podezřelých textů zůstává ${s.zbyva}, projděte je ručně v editoru.`;
    if (s.chyba) txt += ` AI korektura selhala: ${s.chyba}`;
    return txt;
  };

  const spustKorekturu = async (dok) => {
    setInfo("");
    setKorVysledek(null);
    setKorBusy(dok.id);
    const j = await post({ action: "korektura", id: dok.id });
    setKorBusy(null);
    if (!j) return; // chybu už zobrazil post přes setErr
    setKorVysledek({ id: dok.id, text: korekturaZprava(j.stat) });
    await load();
  };

  // Editor hotového dokumentu: seznam nemá `data` (jsou velká), načteme detail.
  // Při otevření se rovnou spočítají kolize s aktuálním dotazníkem (10.9.2026)
  // — editor je zvýrazní a rozbalí dny, kterých se týkají.
  const otevritEditor = async (dok) => {
    setErr(""); setInfo("");
    try {
      const r = await apiFetch(`/api/pdf-jidelnicek?id=${encodeURIComponent(dok.id)}`);
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Dokument se nepodařilo načíst"); return; }
      if (!(j.dokument.data && Array.isArray(j.dokument.data.tydny) && j.dokument.data.tydny.length)) {
        setErr("Tenhle dokument nemá jídelníček k úpravě."); return;
      }
      let konflikty = [];
      try {
        const kr = await apiFetch("/api/pdf-jidelnicek", {
          method: "POST", body: JSON.stringify({ action: "zkontrolovat-zakazy", id: dok.id }),
        });
        const kj = await kr.json();
        if (kj.ok) konflikty = kj.konflikty || [];
      } catch (_) { /* kontrola je bonus, editor funguje i bez ní */ }
      setKontrola(null);
      setEditor({ ...j.dokument, _konflikty: konflikty });
      window.scrollTo({ top: 0 });
    } catch (e) { setErr(e.message || "Dokument se nepodařilo načíst"); }
  };

  // Zavření editoru s neuloženými úpravami se ptá — jinak by práce zmizela.
  const zavritEditor = (neulozeno) => {
    if (!neulozeno) { setEditor(null); return; }
    setConfirmState({
      title: "Zavřít editor bez uložení?",
      description: <>Máte neuložené úpravy jídelníčku. Zavřením se <strong>ztratí</strong> a v dokumentu zůstane původní verze.</>,
      confirmLabel: "Zavřít bez uložení",
      onConfirm: () => setEditor(null),
    });
  };

  const tisk = async (dok) => {
    setErr("");
    try {
      const r = await apiFetch(`/api/pdf-jidelnicek?id=${encodeURIComponent(dok.id)}`);
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Dokument se nepodařilo načíst"); return; }
      const html = buildPdfHtml(j.dokument);
      const w = window.open("", "_blank");
      if (!w) { setErr("Prohlížeč zablokoval nové okno, povolte vyskakovací okna"); return; }
      w.document.open(); w.document.write(html); w.document.close();
    } catch (e) { setErr(e.message || "Náhled se nepodařilo otevřít"); }
  };

  const filtrKlientu = useMemo(() => {
    const q = hledat.trim().toLowerCase();
    if (!q) return [];
    return klienti.filter((c) => (c.klient || "").toLowerCase().includes(q)).slice(0, 8);
  }, [hledat, klienti]);
  const vybranyKlient = klienti.find((c) => {
    const nid = String(c.notion || c.notion_id || "").replace(/-/g, "").slice(-32);
    return nid && klientId.replace(/-/g, "").includes(nid.slice(-12));
  });

  return (
    <Stranka sirka="uzka">
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title || ""}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
        variant="danger"
        onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
        onCancel={() => setConfirmState(null)}
      />

      <GradientHeader
        title="Jídelníček PDF"
        napoveda="Obsáhlý měsíční jídelníček s bonusovými kapitolami. Staví se z dotazníku klientky a je připravený k tisku do PDF."
        icon={BookOpen}
      />

      {editor ? (
        <PdfEditor dok={editor} onZavrit={zavritEditor} onUlozeno={() => load()} />
      ) : (<>

      {/* Nový dokument */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-3">
        <div className="text-sm font-bold text-slate-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Nový dokument</div>
        <div className="flex gap-1.5">
          <button onClick={() => setRezim("klientka")}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${rezim === "klientka" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            Klientka z aplikace
          </button>
          <button onClick={() => setRezim("jednorazovy")}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${rezim === "jednorazovy" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            Jednorázový zákazník
          </button>
        </div>
        {rezim === "jednorazovy" && (
          <div className="space-y-2">
            <input value={jJmeno} onChange={(e) => setJJmeno(e.target.value)} placeholder="Jméno a příjmení zákazníka"
              className="field w-64" />
            <textarea value={jText} onChange={(e) => setJText(e.target.value)} rows={6}
              placeholder="Sem vložte odpovědi zákazníka volným textem (e-mail, zpráva, poznámky): věk, výška, váha, cíl, alergie, co nejí, kolikrát denně chce jíst…"
              className="field w-full" />
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs font-medium text-slate-600 border border-slate-300 rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
                📄 Nahrát .txt nebo PDF
                <input type="file" accept=".txt,text/plain,.pdf,application/pdf" className="hidden"
                  onChange={(e) => { nactiSoubor(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              {jPdf && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                  📎 {jPdf.name}
                  <button onClick={() => setJPdf(null)} className="text-emerald-400 hover:text-rose-600">✕</button>
                </span>
              )}
              <span className="text-[11px] text-slate-400">AI si z textu i PDF (třeba export Google formuláře) sama vytáhne odpovědi dotazníku.</span>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-start gap-2" style={rezim === "jednorazovy" ? { display: "none" } : undefined}>
          <div className="relative">
            <input
              value={vybranyKlient ? vybranyKlient.klient : hledat}
              onChange={(e) => { setHledat(e.target.value); setKlientId(""); }}
              placeholder="Hledat klientku…"
              className="field w-64"
            />
            {!klientId && filtrKlientu.length > 0 && (
              <div className="absolute z-20 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                {filtrKlientu.map((c) => (
                  <button key={c.klient + (c.notion || "")}
                    onClick={() => { const m = String(c.notion || "").match(/[0-9a-f]{32}/i); setKlientId(m ? m[0] : ""); setHledat(c.klient); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50">
                    {c.klient} <span className="text-[11px] text-slate-400">{c.stav || "lead"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={vytvorit} disabled={busy || (rezim === "klientka" ? !klientId : (!jJmeno.trim() || (!jText.trim() && !jPdf)))}
          className="btn btn-primary">
          <Sparkles className="w-4 h-4" /> {busy ? "Zakládám…" : "Pokračovat na návrh příjmu"}
        </button>
        <div className="text-[11px] text-slate-400">
          Nejdřív se ukáže návrh kalorického příjmu a rozložení živin ke kontrole, generování spouštíte až potom.
          Počet chodů se bere z dotazníku (otázka "Jídel denně"). Generování běží několik minut: 28 dní jídel,
          zásoba receptů ke každému chodu, přesné týdenní nákupní seznamy a kapitoly na míru z podkladů blogu.
        </div>
        {info && <div className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5">{info}</div>}
        {err && <div className="text-xs text-rose-600">{err}</div>}
      </div>

      {/* Kontrola před generováním: 1) návrh příjmu, 2) dotazník (15.7.2026) */}
      {/* Úprava upozornění u hotového dokumentu (7.9.2026) — bez přegenerování. */}
      {upozEdit && (
        <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
          onClick={() => setUpozEdit(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Upozornění do PDF</h2>
              <p className="text-xs text-slate-500">{upozEdit.jmeno}, tiskne se na závěrečnou stranu, jídelníček se negeneruje znovu</p>
            </div>
            <div className="p-5 space-y-3">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={upozEdit.zap}
                  onChange={(e) => setUpozEdit((p) => ({ ...p, zap: e.target.checked }))}
                  className="w-4 h-4 accent-plum-600" />
                Vložit do PDF
              </label>
              <textarea rows={5} value={upozEdit.text} maxLength={UPOZORNENI_MAX}
                disabled={!upozEdit.zap}
                onChange={(e) => setUpozEdit((p) => ({ ...p, text: e.target.value }))}
                className="field w-full disabled:bg-slate-100 disabled:text-slate-400" />
              <div className="text-[11px] text-slate-400 tabular-nums">{upozEdit.text.length} / {UPOZORNENI_MAX} znaků</div>
              {!upozEdit.zap && <div className="text-xs text-slate-500">Vypnuté, text zůstane uložený, ale do dokumentu se nevytiskne.</div>}
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setUpozEdit(null)}
                className="text-sm font-semibold text-slate-600 border border-slate-300 rounded-xl px-3 py-2">Zrušit</button>
              <button onClick={ulozitUpozorneni} disabled={busy}
                className="btn btn-primary">
                {busy ? "Ukládám…" : "Uložit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {kontrola && (() => {
        const kc = parseInt(kontrola.pr.kalorie, 10) || 0;
        const pct = (g, nasob) => (kc ? Math.round(((parseInt(g, 10) || 0) * nasob / kc) * 100) : 0);
        const cilText = cilFaktorText(kontrola.ans.cil);
        return (
        <div className="bg-white border-2 border-amber-300 rounded-2xl shadow-sm p-4 space-y-3">
          <div className="text-sm font-bold text-amber-800">
            {kontrola.stav === "hotovo" ? <>📋 Dotazník vygenerovaného jídelníčku{kontrola.jmeno ? `: ${kontrola.jmeno}` : ""}</> : "🔍 Kontrola před generováním"}
          </div>
          {kontrola.stav === "hotovo" && (
            <div className="text-[11px] text-slate-600 bg-plum-50 border border-plum-200 rounded-xl px-3 py-2">
              Jídelníček je už vygenerovaný a úpravou dotazníku se <b>nepřegenerovává</b>. Uložený dotazník řídí další
              úpravy: náhrady jídel z databáze i kontrolu kolizí (po uložení se rovnou spočítá, která jídla novému
              dotazníku odporují). Stará verze dotazníku zůstává v historii dokumentu.
            </div>
          )}

          <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-3 space-y-2">
            <div className="text-xs font-bold text-emerald-800">⚖️ Krok 1: Návrh denního příjmu</div>
            {kontrola.vypocet && kontrola.vypocet.bmr ? (
              <div className="text-[11px] text-slate-600 leading-relaxed">
                Vypočteno metodikou FitWay40: bazální metabolismus (Mifflin-St Jeor) <b>{kontrola.vypocet.bmr} kcal</b> ×
                koeficient aktivity <b>{String(kontrola.vypocet.koef).replace(".", ",")}</b> = celkový výdej <b>{kontrola.vypocet.tdee} kcal</b>,
                cíl {cilText}. Rozložení živin výchozí 25 % bílkoviny / 45 % sacharidy / 30 % tuky. Hodnoty můžete upravit.
              </div>
            ) : (
              <div className="text-[11px] text-amber-700">
                Bez věku, výšky a váhy nejde příjem spočítat. Doplňte je v dotazníku níže a klikněte Přepočítat, nebo zadejte příjem ručně.
              </div>
            )}
            <div className="flex flex-wrap items-end gap-2.5">
              <div>
                <div className="text-[11px] font-semibold text-slate-500 mb-0.5">Kalorie / den</div>
                <input type="number" value={kontrola.pr.kalorie}
                  onChange={(e) => setKontrola((p) => ({ ...p, pr: { ...p.pr, kalorie: e.target.value } }))}
                  onBlur={zmenKcalBlur}
                  className="field w-24 font-bold" />
              </div>
              {[["bilkoviny", "Bílkoviny (g)", 4], ["sacharidy", "Sacharidy (g)", 4], ["tuky", "Tuky (g)", 9]].map(([k, lbl, nasob]) => (
                <div key={k}>
                  <div className="text-[11px] font-semibold text-slate-500 mb-0.5">{lbl}, {pct(kontrola.pr[k], nasob)} %</div>
                  <input type="number" value={kontrola.pr[k]}
                    onChange={(e) => zmenMakro(k, e.target.value)}
                    className="field w-20" />
                </div>
              ))}
              <button type="button" onClick={prepocitatZDotazniku}
                className="text-[11px] font-semibold text-emerald-700 bg-white border border-emerald-300 hover:bg-emerald-100 rounded-lg px-2.5 py-1.5 min-h-[44px] sm:min-h-0">
                ↺ Přepočítat z dotazníku
              </button>
            </div>
            <div className="text-[11px] text-slate-400">Kalorie se dopočítávají z maker (4 kcal/g bílkoviny a sacharidy, 9 kcal/g tuky). Když přepíšete kalorie, makra se po opuštění pole doškálují ve stejném poměru.</div>
          </div>

          <div className="text-xs font-bold text-slate-700">📋 Krok 2: Dotazník</div>
          <div className="text-[11px] text-slate-500">
            Projděte odpovědi, opravte nebo doplňte (hlavně věk, výšku, váhu a cíl).
            U polí {DOT_POLE.map((k) => DOT_LABELS[k]).join(", ")} oddělujte více hodnot čárkou.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.keys(DOT_LABELS).filter((k) => !DOT_MIMO_MRIZKU.includes(k)).map((k) => {
              // Počet jídel denně u vygenerovaného dokumentu nejde měnit —
              // jídelníček už stojí, změna by vyžadovala přegenerování.
              const zamceno = kontrola.stav === "hotovo" && k === "pocet_jidel";
              return (
              <div key={k}>
                <div className="text-[11px] text-slate-400 mb-0.5">{DOT_LABELS[k]}{zamceno ? ", po vygenerování nejde měnit" : ""}</div>
                <input value={kontrola.ans[k] || ""} disabled={zamceno}
                  title={zamceno ? "Počet chodů je daný vygenerovaným jídelníčkem. Jiný počet jídel = nový dokument." : undefined}
                  onChange={(e) => setKontrola((p) => ({ ...p, ans: { ...p.ans, [k]: e.target.value } }))}
                  className={`w-full text-xs border rounded-lg px-2 py-1.5 disabled:bg-slate-100 disabled:text-slate-400 ${["vek", "vyska", "vaha", "cil"].includes(k) && !String(kontrola.ans[k] || "").trim() ? "border-amber-400 bg-amber-50" : "border-slate-200"}`} />
              </div>
              );
            })}
          </div>
          <div className="text-xs font-bold text-slate-700">✍️ Krok 3: Poznámka nutriční terapeutky</div>
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 space-y-1.5">
            <div className="text-[11px] text-emerald-900">
              Co má generátor <b>bezpodmínečně</b> dodržet. Zákazy pište přirozeně, systém je z textu vytáhne
              („nechce výrobky ze sóji, exotické ovoce ani kokos, bez celeru“). Tenhle text má přednost před
              zbytkem dotazníku a projde kontrolou každého receptu.
            </div>
            <textarea rows={4} value={kontrola.ans.poznamka_nutricni || ""}
              onChange={(e) => setKontrola((p) => ({ ...p, ans: { ...p.ans, poznamka_nutricni: e.target.value } }))}
              placeholder="Např.: Nechce sóju, exotické ovoce ani kokos (cukr, olej, mouku). Bez celeru. Snídaně sladké, večeře teplé."
              className="field w-full" />
          </div>

          {/* 7.9.2026: Krok 4 — vlastní upozornění, které se vytiskne do PDF.
              Vědomě fialový (barva dokumentu), aby se nepletl se zeleným Krokem 3:
              ten řídí generátor, tenhle se jen tiskne a AI ho vůbec nevidí. */}
          <div className="text-xs font-bold text-slate-700 flex items-center gap-2 flex-wrap">
            <span>📄 Krok 4: Upozornění do PDF</span>
            <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!kontrola.upozZap}
                onChange={(e) => setKontrola((p) => ({ ...p, upozZap: e.target.checked }))}
                className="w-3.5 h-3.5 accent-plum-600" />
              Vložit do PDF
            </label>
          </div>
          <div className={`rounded-xl border p-3 space-y-1.5 ${kontrola.upozZap ? "border-plum-300 bg-plum-50" : "border-slate-200 bg-slate-50"}`}>
            <div className="text-[11px] text-slate-600">
              Text se vytiskne na <b>závěrečnou stranu</b> jídelníčku. Na obsah nemá vliv, generátor ho nečte.
              {!kontrola.upozZap && <> Teď je vypnutý, text zůstane uložený, jen se do tohohle dokumentu nevytiskne.</>}
            </div>
            <textarea rows={4} value={kontrola.upoz || ""} maxLength={UPOZORNENI_MAX}
              disabled={!kontrola.upozZap}
              onChange={(e) => setKontrola((p) => ({ ...p, upoz: e.target.value }))}
              placeholder="Např.: Jídelníček není náhradou lékařské péče. Při těhotenství, kojení nebo užívání léků jej konzultujte se svým lékařem."
              className="field w-full disabled:bg-slate-100 disabled:text-slate-400" />
            <div className="text-[11px] text-slate-400 tabular-nums">
              {String(kontrola.upoz || "").length} / {UPOZORNENI_MAX} znaků, příště se předvyplní tenhle text
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {kontrola.stav === "hotovo" ? (
              <button onClick={ulozitDotaznikHotoveho} disabled={busy || !String(kontrola.ans.cil || "").trim()}
                className="btn btn-primary">
                <Save className="w-4 h-4" /> {busy ? "Ukládám…" : "Uložit dotazník a zkontrolovat kolize"}
              </button>
            ) : (<>
              <button onClick={potvrditAGenerovat} disabled={busy || !String(kontrola.ans.cil || "").trim()}
                className="btn btn-primary">
                <Sparkles className="w-4 h-4" /> {busy ? "Spouštím…" : "Sedí to, vygenerovat jídelníček"}
              </button>
              <button onClick={ulozitKoncept} disabled={busy}
                className="btn btn-secondary">
                💾 Uložit jako koncept
              </button>
            </>)}
            <button onClick={() => setKontrola(null)}
              className="text-xs text-slate-500 hover:text-slate-700 border border-slate-300 rounded-lg px-3 py-2">
              Zavřít bez uložení
            </button>
          </div>
        </div>
        );
      })()}

      {/* Historie dokumentů (audit) */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-sm font-bold text-slate-700">Historie vygenerovaných jídelníčků</div>
        {celkem > 0 && <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{celkem}</span>}
        <button onClick={() => load()} title="Obnovit" className="dotyk-rozsiren text-slate-400 hover:text-slate-600 p-1"><RefreshCw className="w-3.5 h-3.5" /></button>
        <span className="flex-1" />
        <input value={hledatDoc} onChange={(e) => { setHledatDoc(e.target.value); setOffset(0); }}
          placeholder="🔍 Hledat jméno…"
          className="field w-48" />
      </div>
      <div className="space-y-2">
        {seznam === null ? (
          <div className="text-sm text-slate-400">Načítám…</div>
        ) : seznam.length === 0 ? (
          <div className="text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">Zatím žádný dokument.</div>
        ) : seznam.map((dok) => (
          <div key={dok.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-white border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all rounded-2xl px-3.5 py-3">
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-extrabold flex-shrink-0 ${
              dok.stav === "hotovo" ? "bg-emerald-100 text-emerald-700" : dok.stav === "generuje" ? "bg-brand-100 text-brand-700" : dok.stav === "chyba" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"
            }`}>
              {(String(dok.klient_name || "?").trim().split(/\s+/).map((x) => x[0]).slice(0, 2).join("") || "?").toUpperCase()}
            </span>
            <span className={`text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 border ${STAV_BADGE[dok.stav] || STAV_BADGE.koncept}`}>
              {STAV_LABEL[dok.stav] || dok.stav}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">
                {dok.klient_name}
                {String(dok.klient_id || "").startsWith("jednorazovy") && (
                  <span className="ml-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">jednorázový</span>
                )}
              </div>
              <div className="text-[11px] text-slate-400">
                {dok.pocet_chodu} {sklonChod(dok.pocet_chodu)} denně, založil(a) {dok.autor}, {fmtDT(dok.created_at)}
                {dok.stav === "hotovo" && dok.updated_at !== dok.created_at ? `, vygenerováno ${fmtDT(dok.updated_at)}` : ""}
              </div>
            </div>
            {dok.stav === "generuje" && (() => {
              // Do prvniho ticku (vyber receptu, ~2 min) se % odhaduji z casu,
              // pak prebiraji serverova (13.8.2026, mirror tydenniho generatoru).
              const serverPct = dok.progress && dok.progress.celkem
                ? Math.min(100, (100 * (dok.progress.hotovo || 0)) / Math.max(1, dok.progress.celkem))
                : 0;
              const pct = Math.max(serverPct,
                Math.min(55, odhadPct(dok.created_at ? new Date(dok.created_at).getTime() : null, 300)));
              return (
                <div className="w-full sm:flex-1 sm:min-w-[220px] sm:max-w-md order-last sm:order-none">
                  <AIGenerating pct={pct} />
                </div>
              );
            })()}
            {dok.stav === "chyba" && dok.chyba && (
              <span className="text-[11px] text-rose-600 truncate max-w-[40%]" title={dok.chyba}>{dok.chyba}</span>
            )}
            <span className="ml-auto flex items-center gap-1.5">
              {dok.stav === "hotovo" && (
                <button onClick={() => tisk(dok)}
                  className="btn btn-primary">
                  <Printer className="w-3.5 h-3.5" /> Tiskový náhled
                </button>
              )}
              {dok.stav === "hotovo" && (
                <button onClick={() => otevritEditor(dok)}
                  title="Drobné úpravy jídel bez přegenerování celého dokumentu"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-plum-700 border border-plum-300 bg-plum-50 hover:bg-plum-100 rounded-lg px-2.5 py-1.5">
                  <Pencil className="w-3.5 h-3.5" /> Upravit
                </button>
              )}
              {dok.stav === "hotovo" && (
                <button onClick={() => otevritUpozorneni(dok)}
                  title="Změnit text upozornění, které se tiskne na závěrečnou stranu. Jídelníček se negeneruje znovu."
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-plum-700 border border-plum-300 bg-plum-50 hover:bg-plum-100 rounded-lg px-2.5 py-1.5">
                  📄 Upozornění
                </button>
              )}
              {/* Dotazník vygenerovaného dokumentu (10.9.2026): úprava bez
                  přegenerování — řídí náhrady jídel a kontrolu kolizí. */}
              {dok.stav === "hotovo" && (
                <button onClick={() => otevriKontrolu(dok)}
                  title="Upravit dotazník tohoto jídelníčku (např. nová alergie). Jídelníček se nepřegenerovává, nový dotazník řídí náhrady jídel z databáze a kontrolu kolizí."
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-plum-700 border border-plum-300 bg-plum-50 hover:bg-plum-100 rounded-lg px-2.5 py-1.5">
                  📋 Dotazník
                </button>
              )}
              {/* Korektura zapisuje ROVNOU do DB (na rozdíl od editoru) — píšeme to i do titulku. */}
              {dok.stav === "hotovo" && (
                <button onClick={() => spustKorekturu(dok)} disabled={korBusy === dok.id}
                  title="Pustí českého korektora nad hotovým dokumentem (slovenské výrazy, chybějící diakritika, překlepy, sloh) a opravy ROVNOU uloží do databáze, nic se nepotvrzuje v editoru."
                  className="text-xs font-semibold text-plum-700 border border-plum-300 bg-plum-50 hover:bg-plum-100 rounded-lg px-2.5 py-1.5 disabled:opacity-60 disabled:cursor-wait">
                  {korBusy === dok.id ? "Kontroluji…" : "✍️ Projet korektorem"}
                </button>
              )}
              {dok.stav === "chyba" && (
                <button onClick={async () => { if (await post({ action: "generate", id: dok.id })) await load(); }}
                  className="text-xs font-medium text-brand-700 border border-brand-300 bg-brand-50 hover:bg-brand-100 rounded-lg px-2.5 py-1.5">
                  Zkusit znovu
                </button>
              )}
              {dok.stav === "koncept" && (
                <button onClick={() => otevriKontrolu(dok)}
                  className="text-xs font-medium text-amber-700 border border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-lg px-2.5 py-1.5">
                  🔍 Návrh příjmu a dotazník
                </button>
              )}
              {(["koncept", "chyba"].includes(dok.stav) || isOwner) && dok.stav !== "generuje" && (
                <button onClick={() => smazat(dok)} title={dok.stav === "hotovo" ? "Smazat (jen owner)" : "Smazat koncept"}
                  className="text-slate-300 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
              )}
            </span>
            {korVysledek && korVysledek.id === dok.id && (
              <div className="w-full flex items-start gap-1.5 text-[11px] text-plum-800 bg-plum-50 border border-plum-200 rounded-xl px-2.5 py-1.5">
                <span aria-hidden>✍️</span>
                <span>{korVysledek.text}</span>
                <button onClick={() => setKorVysledek(null)} title="Skrýt"
                  className="ml-auto text-plum-400 hover:text-plum-700 leading-none">×</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {celkem > 10 && (
        <div className="flex items-center justify-center gap-3 pt-1">
          <button onClick={() => setOffset(Math.max(0, offset - 10))} disabled={offset === 0}
            className="btn btn-secondary">
            ‹ Novější
          </button>
          <span className="text-xs text-slate-400 tabular-nums">{offset + 1}–{Math.min(offset + 10, celkem)} z {celkem}</span>
          <button onClick={() => setOffset(offset + 10)} disabled={offset + 10 >= celkem}
            className="btn btn-secondary">
            Starší ›
          </button>
        </div>
      )}

      </>)}
    </Stranka>
  );
}
